import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { sqliteStorage } from './sqliteStorage';
import { offlineStorage } from './offlineStorage';
import { unifiedStorage } from './unifiedStorage';
import toast from 'react-hot-toast';

export interface BackupData {
  version: string;
  timestamp: string;
  orders: any[];
  customers: any[];
  services: any[];
  inventory: any[];
}

class BackupService {
  private readonly BACKUP_VERSION = '1.0';
  private useSQLite: boolean = false;

  async init(): Promise<void> {
    this.useSQLite = await unifiedStorage.isNative();
  }

  async createBackup(): Promise<string> {
    try {
      const isNative = Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios';
      
      // Collect data from storage
      const orders = this.useSQLite ? await sqliteStorage.getOrders() : await offlineStorage.getOrders();
      const customers = this.useSQLite ? await sqliteStorage.getCustomers() : await offlineStorage.getCustomers();
      const services = this.useSQLite ? await sqliteStorage.getServices() : await offlineStorage.getServices();
      const inventory = this.useSQLite ? await sqliteStorage.getInventory() : await offlineStorage.getInventory();

      const backupData: BackupData = {
        version: this.BACKUP_VERSION,
        timestamp: new Date().toISOString(),
        orders,
        customers,
        services,
        inventory,
      };

      const json = JSON.stringify(backupData, null, 2);
      const filename = `hmpos-backup-${new Date().toISOString().split('T')[0]}.json`;

      if (isNative) {
        // Save to device storage on mobile
        await Filesystem.writeFile({
          path: filename,
          data: json,
          directory: Directory.Documents,
          encoding: 'utf8' as any,
        });

        // Share the file
        await Share.share({
          title: 'HMPos Backup',
          text: `Backup created on ${new Date().toLocaleDateString()}`,
          url: filename,
        });

        toast.success('Backup created and saved to Documents');
        return filename;
      } else {
        // Download on web
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast.success('Backup downloaded');
        return filename;
      }
    } catch (error) {
      console.error('Backup failed:', error);
      toast.error('Backup failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
      throw error;
    }
  }

  async restoreBackup(file: File | string): Promise<void> {
    try {
      let json: string;

      if (typeof file === 'string') {
        // File path on mobile
        const result = await Filesystem.readFile({
          path: file,
          directory: Directory.Documents,
          encoding: 'utf8' as any,
        });
        json = result.data as string;
      } else {
        // File object from web
        json = await file.text();
      }

      const backupData: BackupData = JSON.parse(json);

      // Validate backup format
      if (!backupData.version || !backupData.timestamp) {
        throw new Error('Invalid backup file format');
      }

      // Check version compatibility
      if (backupData.version !== this.BACKUP_VERSION) {
        toast('Backup version mismatch. Some features may not work correctly.', { style: { background: '#f59e0b' } });
      }

      // Clear existing data
      await unifiedStorage.clearAll();

      // Restore data
      if (backupData.orders && backupData.orders.length > 0) {
        for (const order of backupData.orders) {
          await unifiedStorage.saveOrder(order);
        }
      }

      if (backupData.customers && backupData.customers.length > 0) {
        for (const customer of backupData.customers) {
          await unifiedStorage.saveCustomer(customer);
        }
      }

      if (backupData.services && backupData.services.length > 0) {
        await unifiedStorage.saveServices(backupData.services);
      }

      if (backupData.inventory && backupData.inventory.length > 0) {
        await unifiedStorage.saveInventory(backupData.inventory);
      }

      toast.success(`Backup restored successfully (${new Date(backupData.timestamp).toLocaleDateString()})`);
    } catch (error) {
      console.error('Restore failed:', error);
      toast.error('Restore failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
      throw error;
    }
  }

  async getBackupInfo(file: File | string): Promise<{
    version: string;
    timestamp: string;
    ordersCount: number;
    customersCount: number;
    servicesCount: number;
    inventoryCount: number;
  }> {
    try {
      let json: string;

      if (typeof file === 'string') {
        const result = await Filesystem.readFile({
          path: file,
          directory: Directory.Documents,
          encoding: 'utf8' as any,
        });
        json = result.data as string;
      } else {
        json = await file.text();
      }

      const backupData: BackupData = JSON.parse(json);

      return {
        version: backupData.version,
        timestamp: backupData.timestamp,
        ordersCount: backupData.orders?.length || 0,
        customersCount: backupData.customers?.length || 0,
        servicesCount: backupData.services?.length || 0,
        inventoryCount: backupData.inventory?.length || 0,
      };
    } catch (error) {
      throw new Error('Failed to read backup file');
    }
  }

  async listBackups(): Promise<string[]> {
    try {
      const isNative = Capacitor.getPlatform() === 'android' || Capacitor.getPlatform() === 'ios';
      
      if (!isNative) {
        return []; // Web doesn't have local file listing
      }

      const result = await Filesystem.readdir({
        path: '',
        directory: Directory.Documents,
      });

      return result.files
        .filter(file => file.name.startsWith('hmpos-backup-') && file.name.endsWith('.json'))
        .map(file => file.name);
    } catch (error) {
      console.error('Failed to list backups:', error);
      return [];
    }
  }

  async deleteBackup(filename: string): Promise<void> {
    try {
      await Filesystem.deleteFile({
        path: filename,
        directory: Directory.Documents,
      });
      toast.success('Backup deleted');
    } catch (error) {
      console.error('Failed to delete backup:', error);
      toast.error('Failed to delete backup');
    }
  }
}

export const backupService = new BackupService();
